import React, { useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaBars, FaSearch, FaTimes } from 'react-icons/fa';
import { RiFileWarningLine } from 'react-icons/ri';
import { motion, AnimatePresence } from 'framer-motion';
import client from '../../sanityClient';
import { debounce } from '../../lib/utils';
import UserAvatar from '../common/UserAvatar';
import { useAuth } from '../AuthContext';

export default function Header() {
    const [searchTerm, setSearchTerm] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();


    const performSearch = async (value) => {
        if (value.length > 2) {
            setIsLoading(true);
            setHasSearched(false);
            try {
                const result = await client.fetch(`
                    *[_type == "post" && (title match $searchTerm || description match $searchTerm)]{
                        title,
                        slug,
                        description
                    }[0...5]
                `, { searchTerm: value + '*' });

                setSuggestions(result);
                setHasSearched(true);
            } catch (error) {
                console.error('Error searching blogs:', error);
                setSuggestions([]);
                setHasSearched(true);
            } finally {
                setIsLoading(false);
            }
        } else {
            setSuggestions([]);
            setHasSearched(false);
        }
    };

    const debouncedSearch = useCallback(
        debounce((value) => performSearch(value), 500),
        []
    );

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
        clearSearch();
    };

    const clearSearch = () => {
        setSearchTerm('');
        setSuggestions([]);
        setHasSearched(false);
    };

    const handleSuggestionClick = (slug) => {
        navigate(`/${slug}`);
        clearSearch();
    };

    const toggleDrawer = () => {
        setIsDrawerOpen(prev => !prev);
    };

    const navItems = [
        { label: 'Home', to: '/' },
        { label: 'Categories', to: '/category' },
    ];

    const drawerVariants = {
        hidden: { x: '100%' },
        visible: { x: 0, transition: { type: 'spring', stiffness: 300, damping: 30 } },
        exit: { x: '100%', transition: { type: 'spring', stiffness: 300, damping: 30 } },
    };

    return (
        <header className="w-full my-4 top-0 z-50">
            <div className="flex flex-col max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap gap-4 sm:gap-6 md:gap-10 w-full items-center justify-between">
                    <Link to="/" className="flex gap-4 sm:gap-7 items-center">
                        <motion.img
                            src="/SalonXLogo.png"
                            className="object-contain w-40 bg-blend-normal aspect-[3.33]"
                            alt="Company Logo"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                        />
                    </Link>
                    <div className="flex flex-row justify-between flex-auto gap-4 sm:gap-6 md:gap-10 items-center">
                        <nav className="hidden md:flex gap-4 text-base font-bold text-neutral-900">
                            {navItems.map((item, index) => (
                                <Link
                                    key={index}
                                    to={item.to}
                                    className={`px-3 py-2 rounded-md ${location.pathname === item.to ? 'text-blue-600' : 'text-neutral-900'}`}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </nav>
                        <div className="relative flex-1 max-w-xs">
                            <form onSubmit={handleSubmit} className="relative">
                                <motion.input
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleInputChange}
                                    placeholder="Search"
                                    className="w-full px-10 py-2 bg-gray-50 rounded-xl border border-gray-200 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    initial={{ scale: 0.95 }}
                                    animate={{ scale: 1 }}
                                    whileFocus={{ scale: 1.02 }}
                                />
                                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                {searchTerm && (
                                    <motion.button
                                        type="button"
                                        onClick={clearSearch}
                                        className="absolute right-3 top-3 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        exit={{ scale: 0 }}
                                    >
                                        <FaTimes />
                                    </motion.button>
                                )}
                            </form>
                            <AnimatePresence>
                                {(isLoading || suggestions.length > 0 || (hasSearched && suggestions.length === 0)) && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
                                    >
                                        {isLoading ? (
                                            <div className="p-4 text-center text-gray-600">
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                    className="inline-block w-6 h-6 border-t-2 border-blue-500 rounded-full"
                                                />
                                            </div>
                                        ) : suggestions.length > 0 ? (
                                            suggestions.map((blog, index) => (
                                                <motion.div
                                                    key={index}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: index * 0.1 }}
                                                    className="group cursor-pointer"
                                                    onClick={() => handleSuggestionClick(blog.slug.current)}
                                                >
                                                    <div className="px-4 py-3 hover:bg-gray-100 transition-colors">
                                                        <p className="font-semibold text-blue-600 group-hover:text-blue-800">
                                                            {blog.title}
                                                        </p>
                                                    </div>
                                                </motion.div>
                                            ))
                                        ) : hasSearched && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="p-4 text-center text-gray-600"
                                            >
                                                <RiFileWarningLine className="mx-auto mb-2 text-3xl text-yellow-500" />
                                                <p>No blog found</p>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <div className="hidden md:block">
                            <UserAvatar />
                        </div>
                        <button className="md:hidden text-2xl text-neutral-900" onClick={toggleDrawer}>
                            <FaBars />
                        </button>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {isDrawerOpen && (
                    <motion.div
                        className="fixed inset-0 z-50 bg-gray-800 bg-opacity-75"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="fixed right-0 top-0 h-full w-64 bg-white shadow-lg"
                            variants={drawerVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                        >
                            <div className="p-4">
                                <button onClick={toggleDrawer} className="text-2xl text-neutral-900 mb-8">
                                    <FaTimes />
                                </button>
                                <nav className="flex flex-col gap-4">
                                    {navItems.map((item, index) => (
                                        <Link
                                            key={index}
                                            to={item.to}
                                            onClick={toggleDrawer}
                                            className={`px-3 py-2 rounded-md ${location.pathname === item.to ? 'text-blue-600' : 'text-neutral-900'}`}
                                        >
                                            {item.label}
                                        </Link>
                                    ))}
                                </nav>
                                <div className="mt-8">
                                    <UserAvatar />
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}